// @flow
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { parsePhoneNumber } from 'libphonenumber-js';
import { Licenses } from 'types/common.js';

import config from '../config/app.config.js';
import { type RequireContext } from '../custom-flow-types';

export const generateId = (method: string) => {
  const date = new Date();
  const timeStamp = `${date.getFullYear()}${date.getMonth()}${date.getDate()}${date.getHours()}${date.getMinutes()}${date.getSeconds()}`;
  return `${method}${timeStamp}`;
};

export const getParameterByName = (name: string, url: string) => {
  const name2 = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${name2}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export function checkPhoneNumber(inputValue: string) {
  if (inputValue.match(/^0[1-59](([ .-])?[0-9]{2}){4}/))
    // __('We cannot accept land-line numbers.')
    return 'We cannot accept land-line numbers.';
  if (!inputValue.match(/^0[67](([ .-])?[0-9]{2}){4}/))
    // __('You have entered an invalid phone number.')
    return 'You have entered an invalid phone number.';
  return null;
}

export function formatPhoneNumber(countryCode: string, inputValue: string) {
  let phoneNumber;
  try {
    phoneNumber = parsePhoneNumber(inputValue, countryCode)?.number;
  } catch (e) {
    phoneNumber = inputValue;
  }
  return phoneNumber;
}

export function setCookie(cname: string, cvalue: string, exmins: number) {
  const d = new Date();
  d.setMinutes(d.getMinutes() + exmins);
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${cname}=${cvalue};${expires};path=${config.cookies.path};${
    config.cookies.secure ? 'secure;' : ''
  }domain=${config.cookies.domain};samesite=strict`;
}

export function getCookie(name: string) {
  if (config.isMockUp) return config.jwt;
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

export function log(...args: any[]) {
  if (config.isDebug) {
    console.log(args);
  }
}

export function importAll(r: RequireContext) {
  const files = {};
  r.keys().forEach((item) => {
    files[item.replace('./', '')] = r(item);
  });
  return files;
}

// // Native form submit on enter wasnt working with react-hook-form. Solution found on redux form thread
// // https://github.com/redux-form/redux-form/issues/572#issuecomment-262618476
export function submitFormOnEnter(e: SyntheticKeyboardEvent<HTMLElement>, cb: Function) {
  if (e.key === 'Enter' && e.shiftKey === false) {
    e.preventDefault();
    cb();
  }
}

// Display goBack arrow only for step 1 and for substeps
export function checkGoBack(step: number, subStep: number, idHasBeenScanned: ?boolean) {
  let shouldDisplayGoBack = true;

  // Whenever subtep is 0 means it's a step landing page
  // Goback is not allowed then except for step 1
  // Goback is not allowed for step 1 if license is FR (for Welcome Bonus Promotion)
  // Goback not allowed as well for id upload substep once information form is filled
  // Goback is not allowed for new limit form substep 3 for FR license
  if (
    (subStep === 0 && step !== 1)
    || (step === 1 && config.license === Licenses.FR)
    || (step === 2 && subStep === 2)
    || (step === 2 && subStep === 1 && (idHasBeenScanned || config.license === Licenses.DE))
    || (step === 4 && subStep === 3 && config.license === Licenses.FR)
  ) {
    shouldDisplayGoBack = false;
  }

  return shouldDisplayGoBack;
}

type comparedItem = { label: string };
export const compare = (a: comparedItem, b: comparedItem): number => {
  // Use toUpperCase() to ignore character casing
  const labelA = a.label.toUpperCase();
  const labelB = b.label.toUpperCase();

  let comparison = 0;
  if (labelA > labelB) {
    comparison = 1;
  } else if (labelA < labelB) {
    comparison = -1;
  }
  return comparison;
};

export const filterOptions = createFilterOptions({
  matchFrom: 'start',
  stringify: option => option.label,
});

export const getCountryCallingCodeByLicense = (license: string) => {
  switch (license) {
    case Licenses.ES:
      return ['34', 'ES'];
    case Licenses.DE:
      return ['49', 'DE'];
    case Licenses.FR:
    default:
      return ['33', 'FR'];
  }
};
